import { ChangeEventHandler, useEffect, useState } from 'react'

import { useUnit } from 'effector-react'
import { EmojiClickData } from 'emoji-picker-react'
import { GIFObject } from 'giphy-api'

import { DIALOG_TYPE } from '@/dialogs/constants'
import { getUserMessageEditorModels } from '@/models/chattingUI/messageEditor/messageEditor'
import { openDialog } from '@/models/dialogs'
import { useEditor } from '@/modules/chatting/ui/chat-window/message-editor/use-editor'

import {
  DROPDOWN_DISAPPEARING_DURATION,
  GIF_LIST_DISAPPEARING_DURATION,
} from './consts'
import { ChattingMessageEditorActionsProps } from './types'

export const useChattingMessageEditorActions = ({
  userId,
  giphyId,
  price,
  onFileChange,
  onSelectGif,
  scheduleDisabled,
}: ChattingMessageEditorActionsProps) => {
  const [isDetachedSchedulePopoverOpen, setIsDetachedSchedulePopoverOpen] =
    useState(false)
  const [isDetachedEmojisPopoverOpen, setIsDetachedEmojisPopoverOpen] =
    useState(false)
  const [isGifsListOpen, setIsGifsListOpen] = useState(false)
  const { handleInsertContent, editor } = useEditor()
  const {
    tags: {
      events: { addTags },
    },
    medias: {
      stores: { $uploadingMedias, $medias },
    },
  } = getUserMessageEditorModels(userId)
  const uploadingMedias = useUnit($uploadingMedias)
  const medias = useUnit($medias)

  const handleFileInputChange: ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    if (event?.target.files) {
      onFileChange(event.target.files)
    }
  }

  const handleVaultOpen = () => {
    openDialog({
      type: DIALOG_TYPE.SELECT_MEDIA_FROM_VAULT,
      isPersistent: true,
      userId,
    })
  }

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    if (editor?.isInitialized) {
      handleInsertContent(emojiData.emoji)
    }
  }

  const handleClickTag = () => {
    openDialog({
      type: DIALOG_TYPE.TAG_OTHER_CREATORS_AND_RELEASE_FORMS,
      onAddTags: addTags,
    })
  }

  const handleClickCountdown = () => {
    openDialog({
      type: DIALOG_TYPE.COUNTDOWN,
    })
  }

  const handleClickGif = () => {
    if (!isGifsListOpen) {
      setIsGifsListOpen(true)
    }
  }

  const handleClickOverflowedGif = () => {
    setTimeout(() => {
      handleClickGif()
    }, DROPDOWN_DISAPPEARING_DURATION)
  }

  const onGifsListOpenChange = (state: boolean) => {
    setTimeout(() => {
      setIsGifsListOpen(state)
    }, GIF_LIST_DISAPPEARING_DURATION)
  }

  const handleSelectGif = (id: GIFObject['id']) => {
    onSelectGif(id)
    setIsGifsListOpen(false)
  }

  const handleClickOverflowedSchedule = () => {
    if (scheduleDisabled) {
      return
    }
    setTimeout(() => {
      setIsDetachedSchedulePopoverOpen(true)
    }, DROPDOWN_DISAPPEARING_DURATION)
  }

  const handleClickOverflowedEmojis = () => {
    setTimeout(() => {
      setIsDetachedEmojisPopoverOpen(true)
    }, DROPDOWN_DISAPPEARING_DURATION)
  }

  const canMediaBeAdded = !giphyId
  const canGifBeAdded =
    !medias.length && !uploadingMedias.length && !giphyId && !price
  const canPriceBeAdded = !giphyId

  useEffect(() => {
    if (scheduleDisabled) {
      setIsDetachedSchedulePopoverOpen(false)
    }
  }, [scheduleDisabled])

  return {
    handleFileInputChange,
    handleVaultOpen,
    handleEmojiClick,
    handleClickTag,
    handleClickCountdown,
    handleSelectGif,
    handleClickGif,
    onGifsListOpenChange,
    handleClickOverflowedGif,
    handleClickOverflowedSchedule,
    handleClickOverflowedEmojis,
    setIsDetachedSchedulePopoverOpen,
    setIsDetachedEmojisPopoverOpen,
    isGifsListOpen,
    isDetachedSchedulePopoverOpen,
    isDetachedEmojisPopoverOpen,
    canMediaBeAdded,
    canGifBeAdded,
    canPriceBeAdded,
  }
}
